import { formatInTimeZone, toZonedTime } from 'date-fns-tz'; // Changed utcToZonedTime to toZonedTime
import { parseISO, isValid, format as formatSimple } from 'date-fns';
import { useAuthStore } from '../store/authStore'; // Adjust path if needed

// Define standard format strings for consistency
export const DATE_FORMAT_SHORT = 'MMM d, yyyy';
export const DATE_FORMAT_LONG = 'MMMM d, yyyy';
export const DATETIME_FORMAT_SHORT = 'MMM d, yyyy, h:mm a';
export const DATETIME_FORMAT_COMPACT = 'MMM d, h:mma'; // Compact format for cards: "Aug 23, 3:12AM"
export const DATETIME_FORMAT_MEDIUM = 'MMM d, yyyy, h:mm:ss a';
export const DATETIME_FORMAT_LONG = 'MMMM d, yyyy, h:mm:ss a zzzz'; // Includes timezone name

/**
 * Parses various date inputs into a Date object.
 * Handles ISO strings, Date objects, and Unix timestamps (assuming milliseconds).
 * @param dateInput The date input.
 * @returns A Date object or null if invalid.
 */
const parseDateInput = (dateInput: string | Date | number | null | undefined): Date | null => {
    if (dateInput === null || dateInput === undefined) {
        // console.warn(`[parseDateInput] Received null or undefined input.`); // Keep console clean unless debugging
        return null;
    }

    let dateObj: Date | null = null;
    const inputType = typeof dateInput;

    try {
        if (inputType === 'string') {
            // Attempt to parse as ISO 8601 string first
            dateObj = parseISO(dateInput as string);
            // If ISO parsing fails, check if it's a string representation of a number (timestamp)
            if (!isValid(dateObj)) {
                const numericTimestamp = Number(dateInput);
                if (!isNaN(numericTimestamp) && isFinite(numericTimestamp) && numericTimestamp > 0) {
                    // Ensure it's treated as milliseconds
                    dateObj = new Date(numericTimestamp);
                } else {
                     console.warn(`[parseDateInput] String input is not a valid ISO date or numeric timestamp:`, dateInput);
                     dateObj = null; // Ensure it's null if parsing failed
                }
            }
        } else if (inputType === 'number') {
            // Explicitly convert to Number to handle potential bigint and satisfy isFinite/new Date
            const numericValue = Number(dateInput);

            // Assume Unix timestamp in milliseconds
            // Check for valid number range, although Date constructor handles large numbers
             if (isFinite(numericValue) && numericValue > 0) { // Basic check using the converted number
                 dateObj = new Date(numericValue); // Use the converted number
             } else {
                 console.warn(`[parseDateInput] Received non-positive, non-finite, or potentially problematic numeric input:`, dateInput);
                 dateObj = null; // Ensure it's null
             }
        } else if (dateInput instanceof Date) {
            // Ensure the Date object itself is valid
            if (isValid(dateInput)) {
                dateObj = dateInput;
            } else {
                 console.warn(`[parseDateInput] Received an invalid Date object:`, dateInput);
                 dateObj = null;
            }
        } else {
             console.warn(`[parseDateInput] Received unexpected input type '${inputType}':`, dateInput);
             dateObj = null; // Ensure it's null for unexpected types
        }

        // Final validation check
        if (!dateObj || !isValid(dateObj)) {
            // Only log if we expected a valid dateObj but it turned out invalid
            if (dateObj !== null) {
                 console.warn(`[parseDateInput] Failed to create a valid Date object. Input type: '${inputType}', Input value:`, dateInput, "Resulting object:", dateObj);
            }
            return null; // Return null if not valid
        }

        return dateObj;

    } catch (error) {
        console.error(`[parseDateInput] Error during parsing. Input type: '${inputType}', Input value:`, dateInput, error);
        return null;
    }
}

/**
 * Formats a date/time input according to the user's stored timezone.
 * Falls back to UTC if the user's timezone is not set.
 * Handles ISO strings, Date objects, and Unix timestamps (ms).
 *
 * @param dateInput The date string (ISO 8601 recommended), Date object, or timestamp number (ms).
 * @param formatString The date-fns format string (e.g., DATETIME_FORMAT_SHORT).
 * @param fallbackValue String to return if dateInput is null/undefined/invalid. Defaults to an empty string.
 * @returns Formatted date string or the fallbackValue.
 */
export const formatUserDateTime = (
  dateInput: string | Date | number | null | undefined,
  formatString: string = DATETIME_FORMAT_SHORT, // Sensible default
  fallbackValue: string = ''
): string => {
  const rawUserTimezone = useAuthStore.getState().userTimezone;
  let targetTimezone = rawUserTimezone || 'UTC'; // Default to UTC if null/undefined

  const dateObj = parseDateInput(dateInput); // Use the updated parser

  if (!dateObj) {
    // Avoid logging every time fallback is used for potentially valid null/undefined inputs
    // Only log if the original input was *not* null/undefined but still resulted in no dateObj
    if (dateInput !== null && dateInput !== undefined) {
        // console.warn(`[formatUserDateTime] Using fallback value '${fallbackValue}' due to invalid/unparseable date input:`, dateInput);
    }
    return fallbackValue;
  }

  // Explicitly validate the targetTimezone before using it with formatInTimeZone
  // This is important if rawUserTimezone could be a non-null, invalid string.
  if (targetTimezone !== 'UTC') { // Only validate if not already UTC
    try {
      // Attempt a benign operation with the targetTimezone to see if it's valid.
      // new Date() creates a valid date object to test with.
      toZonedTime(new Date(), targetTimezone);
    } catch (tzError) {
      console.warn(`[formatUserDateTime] Invalid userTimezone ('${rawUserTimezone}'). Falling back to UTC. Error: ${tzError instanceof Error ? tzError.message : String(tzError)}`);
      targetTimezone = 'UTC'; // Force UTC if user's timezone is problematic
    }
  }

  try {
    // formatInTimeZone converts the date to the target timezone *before* formatting
    return formatInTimeZone(dateObj, targetTimezone, formatString);
  } catch (error) {
    // This catch block should now be less likely to be hit due to invalid timezones,
    // but could still occur for other reasons (e.g., issues with dateObj or formatString).
    console.error(`[formatUserDateTime] Error formatting date for timezone ${targetTimezone}. Input:`, dateInput, "Parsed Date:", dateObj, "Error:", error);
    // Fallback to simple UTC formatting if timezone formatting fails
    try {
        // Format the original valid date object in UTC
        return formatSimple(dateObj, formatString) + ' (UTC)'; // Indicate fallback
    } catch (innerError) {
        console.error(`[formatUserDateTime] Error during fallback UTC formatting. Parsed Date:`, dateObj, innerError);
        return 'Invalid Date'; // Final fallback
    }
  }
};

/**
 * Converts a UTC date/time input to the user's timezone WITHOUT changing the time value.
 * Useful for displaying times that are inherently UTC but need local context (less common).
 * Example: If input is 10:00 UTC, and user is in EST (UTC-5), output might be formatted as 10:00 EST.
 * Use formatUserDateTime for most cases where you want the actual local time (e.g., 10:00 UTC becomes 05:00 EST).
 *
 * @param dateInput The date string (ISO 8601 recommended), Date object, or timestamp number (ms).
 * @param formatString The date-fns format string.
 * @param fallbackValue String to return if dateInput is null/undefined/invalid. Defaults to an empty string.
 * @returns Formatted date string or the fallbackValue.
 */
export const displayUtcInUserTz = (
    dateInput: string | Date | number | null | undefined,
    formatString: string = DATETIME_FORMAT_SHORT,
    fallbackValue: string = ''
): string => {
    const userTimezone = useAuthStore.getState().userTimezone;
    const targetTimezone = userTimezone || 'UTC';

    const dateObj = parseDateInput(dateInput);

    if (!dateObj) {
        return fallbackValue;
    }

    try {
        // toZonedTime gives the Date object representing the same instant in the target zone
        const zonedDate = toZonedTime(dateObj, targetTimezone); // Changed utcToZonedTime to toZonedTime
        // Then format that zoned date
        return formatSimple(zonedDate, formatString); // Use simple format as timezone is now inherent
    } catch (error) {
        console.error(`[displayUtcInUserTz] Error converting UTC date to timezone ${targetTimezone}. Input:`, dateInput, error);
         try {
            return formatSimple(dateObj, formatString) + ' (UTC)'; // Indicate fallback
        } catch {
            return 'Invalid Date'; // Final fallback
        }
    }
};